import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, Text, Title, Stack, Group, TextInput, Table, ActionIcon, Modal, Select, Loader, Progress } from '@mantine/core';
import { IconPlus, IconTrash, IconPencil, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { User, UpsertUser } from '../services/usersService';
import { listUsers, createUser, deleteUser, updateUser } from '../services/usersService';
import { listCustomers } from '../services/customersService';

const UsersPage: React.FC = () => {
  const translation = useTranslation();
  const t = translation?.t || ((key: string) => key);
  const [items, setItems] = useState<User[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<UpsertUser>({ firstName: '', lastName: '', email: '', customerId: '' });
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    customerId: ''
  });
  
  const emptyForm: UpsertUser = { firstName: '', lastName: '', email: '', customerId: '' };

  const load = async () => {
    setLoading(true);
    setError(null);
    setLoadingStage(t('common.loading.users') || 'Loading users...');
    
    try {
      setLoadingStage(t('common.loading.data') || 'Loading data...');
      const [u, c] = await Promise.all([listUsers(), listCustomers()]);
      
      setLoadingStage(t('common.loading.processing') || 'Processing...');
      setItems(u.items);
      setCustomers(c.items.map((x) => ({ value: x.id, label: x.company })));
      
      setLoadingStage(t('common.loading.complete') || 'Complete');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || 'Failed to load users');
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  useEffect(() => { load(); }, []);

  // Filter items based on filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Text filters (case-insensitive)
      if (filters.firstName && !item.firstName.toLowerCase().includes(filters.firstName.toLowerCase())) return false;
      if (filters.lastName && !item.lastName.toLowerCase().includes(filters.lastName.toLowerCase())) return false;
      if (filters.email && !item.email.toLowerCase().includes(filters.email.toLowerCase())) return false;
      
      // Select filter
      if (filters.customerId && item.customerId !== filters.customerId) return false;
      
      return true;
    });
  }, [items, filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      firstName: '',
      lastName: '',
      email: '',
      customerId: ''
    });
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (u: User) => { 
    setEditingId(u.id); 
    setForm({ 
      firstName: u.firstName || '', 
      lastName: u.lastName || '', 
      email: u.email || '', 
      customerId: u.customerId || '' 
    }); 
    setModalOpen(true); 
  };

  const onSubmit = async () => {
    if (editingId) await updateUser(editingId, form); else await createUser(form);
    setModalOpen(false);
    await load();
  };

  const onDelete = async (id: string) => {
    if (!confirm('Delete this user?')) return;
    await deleteUser(id);
    await load();
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>{t('navigation.users')}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>{t('common.new') || 'Neu'}</Button>
      </Group>

      {/* Filters Section */}
      <Card withBorder>
        <Title order={4} mb="md">{t('common.filters') || 'Filters'}</Title>
        <Stack gap="sm">
          {/* First row of filters */}
          <Group grow>
            <TextInput
              label={t('users.firstName') || 'First name'}
              placeholder="Filter by first name..."
              value={filters.firstName}
              onChange={(e) => setFilters(f => ({ ...f, firstName: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <TextInput
              label={t('users.lastName') || 'Last name'}
              placeholder="Filter by last name..."
              value={filters.lastName}
              onChange={(e) => setFilters(f => ({ ...f, lastName: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <TextInput
              label={t('users.email') || 'Email'}
              placeholder="Filter by email..."
              value={filters.email}
              onChange={(e) => setFilters(f => ({ ...f, email: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
          </Group>
          
          {/* Second row of filters */}
          <Group grow>
            <Select
              label={t('users.customer') || 'Customer'}
              placeholder="Filter by customer..."
              value={filters.customerId}
              onChange={(v) => setFilters(f => ({ ...f, customerId: v || '' }))}
              data={customers}
              clearable
            />
            <Button variant="outline" onClick={clearFilters} style={{ marginTop: '24px' }}>
              {t('common.clear') || 'Clear'}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        {error && (
          <Text c="red" mb="sm">{error}</Text>
        )}
        {loading && (
          <Stack align="center" justify="center" style={{ minHeight: 300 }}>
            <Loader size="xl" color="blue" />
            
            <Stack gap="sm" align="center" style={{ minWidth: 300 }}>
              <Text size="lg" fw={500}>
                {t('common.loading') || 'Loading...'}
              </Text>
              
              {loadingStage && (
                <Text size="sm" c="dimmed">
                  {loadingStage}
                </Text>
              )}
            </Stack>
          </Stack>
        )}
        {!loading && filteredItems.length === 0 ? (
          <Text c="dimmed">
            {items.length === 0 
              ? (t('users.noUsers') || 'No users yet')
              : (t('users.noUsers') || 'No users match your filters')
            }
          </Text>
        ) : (
          !loading && (
            <Table striped withRowBorders={false} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('users.firstName') || 'First name'}</Table.Th>
                <Table.Th>{t('users.lastName') || 'Last name'}</Table.Th>
                <Table.Th>{t('users.email') || 'Email'}</Table.Th>
                <Table.Th>{t('users.customer') || 'Customer'}</Table.Th>
                <Table.Th style={{ width: 80 }}>{t('users.actions') || 'Actions'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>{u.firstName}</Table.Td>
                  <Table.Td>{u.lastName}</Table.Td>
                  <Table.Td>{u.email}</Table.Td>
                  <Table.Td>{customers.find(c => c.value === u.customerId)?.label || u.customerId}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="light" onClick={() => openEdit(u)}><IconPencil size={16} /></ActionIcon>
                      <ActionIcon color="red" variant="light" onClick={() => onDelete(u.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          )
        )}
      </Card>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? (t('users.edit') || 'Edit user') : (t('users.create') || 'Create user')}>
        <Stack>
          <Group grow>
            <TextInput label={t('users.firstName') || 'First name'} value={form.firstName || ''} onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} />
            <TextInput label={t('users.lastName') || 'Last name'} value={form.lastName || ''} onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} />
          </Group>
          <TextInput label={t('users.email') || 'Email'} type="email" value={form.email || ''} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
          <Select
            label={t('users.customer') || 'Customer'}
            data={customers}
            value={form.customerId || ''}
            onChange={(v: string | null) => setForm(f => ({ ...f, customerId: v || '' }))}
            searchable
          />
          <Group justify="flex-end"><Button onClick={onSubmit}>{t('common.save') || 'Speichern'}</Button></Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default UsersPage;
